/**
 * Making a project: four questions, each answered against what the app found.
 *
 * The second step is the one that earns the wizard. Picking a folder runs the
 * data scan there and then, so the cohort is decided while looking at what is
 * actually in it — and a subject left out carries the scan's own words as the
 * reason. Everywhere else, screening the data and deciding who is in are two
 * activities with a spreadsheet and somebody's memory in between.
 */

import { AlertTriangle, ArrowLeft, ArrowRight, Check, FolderOpen, Loader2, X } from "lucide-react";

import { Button, Input, Select } from "@/components/ui/primitives";
import { DialogOverlay, DialogPanel, dialogPrimaryButtonClass, dialogSecondaryButtonClass } from "@/components/ui/dialog";
import { pickDirectory } from "@/ipc/api";
import type { CohortSubject, PipelineKind, StageSelection } from "@/ipc/contract";
import { cn } from "@/lib/utils";
import { fullSelection, stagesFor } from "@/pipeline/stages";
import { useAppStore } from "@/store/appStore";
import { partition, STEPS, useProjectDraft, type Step } from "./store";

const KIND_LABEL: Record<PipelineKind, string> = {
  dwi: "DWI — stroke core",
  pwi: "PWI — hypoperfusion",
  combined: "DWI + PWI — core, hypoperfusion and mismatch",
};

function StepRail({ step, go }: { step: Step; go: (s: Step) => void }) {
  const index = STEPS.findIndex((s) => s.id === step);
  return (
    <div className="flex items-center gap-1 border-b border-ads-border px-5 py-2.5">
      {STEPS.map((s, i) => {
        const state = i < index ? "done" : i === index ? "here" : "ahead";
        return (
          <button
            key={s.id}
            type="button"
            disabled={state === "ahead"}
            onClick={() => go(s.id)}
            className={cn(
              "flex items-center gap-1.5 rounded px-2 py-1 text-[11px] transition",
              state === "here" && "bg-ads-accent-soft font-medium text-ads-accent",
              state === "done" && "text-neutral-600 hover:bg-ads-hover",
              state === "ahead" && "cursor-default text-neutral-300",
            )}
          >
            <span className="tabular-nums">{i + 1}</span>
            {s.label}
            {state === "done" ? <Check className="h-3 w-3 text-emerald-600" /> : null}
          </button>
        );
      })}
    </div>
  );
}

function SubjectRow({
  subject,
  excluded,
  reason,
  onExclude,
  onInclude,
}: {
  subject: CohortSubject;
  excluded: boolean;
  reason?: string;
  onExclude: (reason: string, fromScan: boolean) => void;
  onInclude: () => void;
}) {
  const worst = subject.issues.find((i) => i.severity === "error") ?? subject.issues[0];
  return (
    <div
      className={cn(
        "flex items-start gap-2.5 border-b border-ads-border px-3 py-1.5 last:border-0",
        excluded && "bg-ads-bg",
      )}
    >
      <input
        type="checkbox"
        checked={!excluded}
        aria-label={`Include ${subject.subjectId}`}
        onChange={(e) =>
          e.target.checked
            ? onInclude()
            : onExclude(worst?.message ?? "Excluded by the reviewer", Boolean(worst))
        }
        className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-ads-accent"
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block truncate text-[12px] font-medium",
            excluded ? "text-neutral-400 line-through" : "text-neutral-800",
          )}
        >
          {subject.subjectId}
        </span>
        {excluded ? (
          // The reason is editable here because only the reviewer knows the
          // medical half of it; the scan's wording is a starting point.
          <Input
            className="mt-1 h-6 text-[11px]"
            value={reason ?? ""}
            placeholder="Why is this subject out?"
            onChange={(e) => onExclude(e.target.value, false)}
          />
        ) : worst ? (
          <span className="block truncate text-[10.5px] text-amber-700">{worst.message}</span>
        ) : (
          <span className="block text-[10.5px] text-neutral-400">
            {subject.roles.join(" · ")}
          </span>
        )}
      </span>
    </div>
  );
}

export function NewProject() {
  const d = useProjectDraft();
  const defaultRoot = useAppStore((s) => s.preferences?.defaultInputRoot ?? null);
  if (!d.open) return null;

  const { included, excluded } = partition(d.scan, d.excluded);
  const index = STEPS.findIndex((s) => s.id === d.step);
  const canGoOn =
    d.step === "question" ||
    (d.step === "data" && d.scan != null && included.length > 0) ||
    (d.step === "protocol" && d.stages.dwi.length + d.stages.pwi.length > 0) ||
    d.step === "name";

  const choose = async () => {
    const { path } = await pickDirectory(
      "Choose the folder that holds your subjects",
      defaultRoot ?? undefined,
    );
    if (path) await d.chooseRoot(path);
  };

  const setKind = (kind: PipelineKind) => d.set({ pipelineKind: kind, stages: fullSelection(kind) });

  return (
    <DialogOverlay
      className="z-50"
      role="dialog"
      aria-modal="true"
      aria-label="New project"
      onClick={d.close}
    >
      <DialogPanel className="max-h-[86vh] w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pb-4 pt-6">
          <div>
            <h2 className="text-[17px] font-semibold tracking-tight text-ads-text">New project</h2>
            <p className="mt-1 text-[13px] text-ads-muted">{STEPS[index].asks}</p>
          </div>
          <button
            type="button"
            onClick={d.close}
            aria-label="Close"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-black/[0.05] hover:text-neutral-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <StepRail step={d.step} go={d.go} />

        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
          {/* ------------------------------------------------ question --- */}
          {d.step === "question" ? (
            <div className="flex flex-col gap-3">
              <textarea
                autoFocus
                value={d.question}
                onChange={(e) => d.set({ question: e.target.value })}
                placeholder="Does a mismatch ratio above 1.8 predict mRS ≤ 2 at 90 days?"
                className="h-28 w-full resize-none rounded border border-ads-border-strong bg-ads-surface px-3 py-2 text-[13px] leading-relaxed text-ads-text focus:border-ads-accent focus:outline-none"
              />
              <p className="text-[11.5px] leading-relaxed text-neutral-500">
                This is the line everyone needs and nobody writes down. It sits at the top of the
                project from here on, in your words — the app never rewrites it, and leaving it
                empty is allowed.
              </p>
            </div>
          ) : null}

          {/* ---------------------------------------------------- data --- */}
          {d.step === "data" ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Button variant={d.scan ? "outline" : "primary"} onClick={() => void choose()} disabled={d.busy}>
                  {d.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FolderOpen className="h-3.5 w-3.5" />}
                  {d.scan ? "Another folder…" : "Choose folder…"}
                </Button>
                {d.root ? (
                  <span className="min-w-0 flex-1 truncate font-mono text-[10.5px] text-neutral-400">
                    {d.root}
                  </span>
                ) : null}
              </div>

              {d.error ? (
                <p className="whitespace-pre-line rounded border border-red-200 bg-red-50 px-3 py-2 text-[11.5px] leading-relaxed text-red-800">
                  {d.error}
                </p>
              ) : null}

              {d.scan ? (
                <>
                  <p className="rounded border border-ads-border bg-ads-subtle px-3 py-2 text-[11.5px] leading-relaxed text-neutral-700">
                    {d.scan.subjectCount} subjects read. <strong>{included.length} in</strong>,{" "}
                    {excluded.length} out.
                    {d.scan.withErrors > 0 ? (
                      <>
                        {" "}
                        {d.scan.withErrors === 1
                          ? "The one with an error-level finding is"
                          : `The ${d.scan.withErrors} with error-level findings are`}{" "}
                        excluded by default, with the scan&rsquo;s wording as the reason — put any
                        of them back if you disagree.
                      </>
                    ) : null}
                  </p>
                  <div className="max-h-[38vh] overflow-auto rounded border border-ads-border">
                    {d.scan.subjects.map((s) => (
                      <SubjectRow
                        key={s.subjectId}
                        subject={s}
                        excluded={s.subjectId in d.excluded}
                        reason={d.excluded[s.subjectId]?.reason}
                        onExclude={(reason, fromScan) => d.exclude(s.subjectId, reason, fromScan)}
                        onInclude={() => d.include(s.subjectId)}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          ) : null}

          {/* ------------------------------------------------ protocol --- */}
          {d.step === "protocol" ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <span className="text-[11px] font-medium text-neutral-700">Pipeline</span>
                <Select
                  value={d.pipelineKind}
                  onChange={(e) => setKind(e.target.value as PipelineKind)}
                  className="max-w-md"
                >
                  {(["dwi", "pwi", "combined"] as PipelineKind[]).map((k) => (
                    <option key={k} value={k}>
                      {KIND_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <span className="text-[11px] font-medium text-neutral-700">Stages</span>
                <div className="mt-1.5 grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                  {(["DWI", "PWI"] as const)
                    .filter((m) => (m === "DWI" ? d.pipelineKind !== "pwi" : d.pipelineKind !== "dwi"))
                    .map((modality) => {
                      const key = modality.toLowerCase() as "dwi" | "pwi";
                      // Widened once: `StageSelection` types each list to its
                      // own modality, and this loop deliberately renders both.
                      const chosen: string[] = d.stages[key];
                      return (
                        <div key={modality}>
                          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                            {modality}
                          </div>
                          {stagesFor(modality).map((stage) => (
                            <label
                              key={stage.id}
                              className="flex items-baseline gap-2 py-0.5 text-[11.5px] text-neutral-700"
                            >
                              <input
                                type="checkbox"
                                className="h-3 w-3 accent-ads-accent"
                                checked={chosen.includes(stage.id)}
                                onChange={(e) =>
                                  d.set({
                                    stages: {
                                      ...d.stages,
                                      // The two lists are typed per modality;
                                      // this loop is generic over both, so the
                                      // id is widened once here rather than
                                      // splitting the rendering in two.
                                      [key]: (e.target.checked
                                        ? [...chosen, stage.id]
                                        : chosen.filter((x) => x !== stage.id)) as string[],
                                    } as StageSelection,
                                  })
                                }
                              />
                              {stage.label}
                            </label>
                          ))}
                        </div>
                      );
                    })}
                </div>
              </div>

              <p className="flex items-start gap-2 rounded border border-ads-accent-border bg-ads-accent-soft px-3 py-2 text-[11.5px] leading-relaxed text-neutral-700">
                <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-ads-accent" />
                <span>
                  This is what <strong>every</strong> subject in the cohort will receive. It is
                  recorded with the project, and a run that does something else is reported as a
                  deviation rather than counted with the rest.
                </span>
              </p>
            </div>
          ) : null}

          {/* ---------------------------------------------------- name --- */}
          {d.step === "name" ? (
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-medium text-neutral-700">Project name</span>
                <Input value={d.name} onChange={(e) => d.set({ name: e.target.value })} className="max-w-md" />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font=medium text-neutral-700">Output folder</span>
                <Input
                  value={d.outputRoot}
                  onChange={(e) => d.set({ outputRoot: e.target.value })}
                  className="max-w-xl font-mono text-[11.5px]"
                />
                <span className="text-[10.5px] text-neutral-400">
                  Results land under &lt;output folder&gt;/&lt;subject&gt;/. It must not be inside
                  the folder your data is in.
                </span>
              </label>

              <div className="mt-2 rounded border border-ads-border bg-ads-subtle px-3.5 py-3 text-[11.5px] leading-relaxed text-neutral-700">
                <div className="font-medium text-neutral-900">{d.name || "Untitled project"}</div>
                {d.question ? (
                  <div className="mt-1 italic text-neutral-600">“{d.question}”</div>
                ) : null}
                <div className="mt-2 tabular-nums text-neutral-500">
                  {included.length} subjects in · {excluded.length} out ·{" "}
                  {d.stages.dwi.length + d.stages.pwi.length} stages ·{" "}
                  {d.pipelineKind.toUpperCase()}
                </div>
              </div>
              {d.error ? <p className="text-[11.5px] text-red-700">{d.error}</p> : null}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2 border-t border-black/[0.06] bg-neutral-50/60 px-6 py-4">
          {index > 0 ? (
            <Button className={dialogSecondaryButtonClass} onClick={() => d.go(STEPS[index - 1].id)}>
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </Button>
          ) : null}
          <span className="flex-1" />
          <Button className={dialogSecondaryButtonClass} onClick={d.close}>Cancel</Button>
          {index < STEPS.length - 1 ? (
            <Button variant="primary" className={dialogPrimaryButtonClass} disabled={!canGoOn} onClick={() => d.go(STEPS[index + 1].id)}>
              Next
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button variant="primary" className={dialogPrimaryButtonClass} disabled={d.busy || !d.scan} onClick={() => void d.create()}>
              {d.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Create project
            </Button>
          )}
        </div>
      </DialogPanel>
    </DialogOverlay>
  );
}
